import React, { useState, useRef, useEffect } from "react";

import {
  canUseAiRemixCustomTemplates,
  remixAiUpgradePlan,
} from "../../../utils/canUseAiRemixText";

const RemixUploader = (props) => {
  const {
    subtitle,
    filestackApiKey,
    environment,
    websiteId,
    onUploadStart,
    onUploadProcessing,
    onUploadSuccess,
    onRemixFromChat,
    uploadUrl = true,
    uploadImage = true,
    website,
    subscription,
    account,
    current_user,
    showRemixFromChat = false,
  } = props;

  const [remixLoading, setRemixLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    props.fromScratch !== false ? "scratch" : "image"
  );
  const remixSubmitBtn = useRef(null);
  const inputError = useRef(null);
  const imageInput = useRef(null);
  const subtitleRef = useRef(null);
  const mountedRef = useRef(true);
  const dropdownRef = useRef(null);

  const defaultSubtitle =
    subtitle ||
    (showRemixFromChat
      ? "Start from scratch, upload an image, input a website URL, or remix from chat to generate a template with AI"
      : "Start from scratch, upload an image, or input a website URL to generate a template with AI");

  // Feature gate logic for Remix AI uploads / custom generation
  const canUseRemixAI = canUseAiRemixCustomTemplates({
    currentUser: current_user,
    website,
    subscription,
    account,
  });

  // Remix from Chat requires base Remix AI access
  const canUseRemixFromChat = canUseRemixAI;

  const getRequiredPlan = () => {
    const accountPlanType = subscription?.plan_type || "free";
    const isFreeAccount = accountPlanType === "free";
    const isRemixPlan =
      accountPlanType &&
      (accountPlanType.startsWith("2025_") ||
        accountPlanType.startsWith("2026_"));
    const is2024ProWebsite = website?.plan_type === "2024_pro";

    if (
      String(accountPlanType || "").includes("2024") ||
      String(website?.plan_type || "").includes("2024")
    ) {
      return "remix";
    }
    if (is2024ProWebsite) {
      return "plus";
    }
    if (!isFreeAccount && !isRemixPlan) {
      return "plus";
    }
    return remixAiUpgradePlan({ subscription, website });
  };

  const getRequiredPlanForRemixFromChat = () =>
    remixAiUpgradePlan({ subscription, website });

  const handleFeatureGate = (event, feature) => {
    const limit = Number(account?.billable_actions_limit);
    const usage = Number(account?.billable_actions_usage);
    const freePlan = String(subscription?.plan_type || "").trim() === "free";
    const exhausted =
      Number.isFinite(limit) &&
      limit > 0 &&
      Number.isFinite(usage) &&
      usage >= limit;

    const requiredPlan = getRequiredPlan();
    subscriptions.upgrade_modal(
      event,
      false,
      exhausted
        ? freePlan
          ? `continue with Remix AI after using all ${limit} included AI actions.`
          : `continue with Remix AI after using all ${limit} actions this month.`
        : `use remix AI to generate a template`,
      requiredPlan,
      exhausted
        ? "Triggered feature CTA – Remix AI actions limit"
        : `Triggered feature CTA – Remix AI ${feature}`,
      false,
      "x3rs6cvrwi"
    );
  };

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOptionSelect = (option, event) => {
    // Check feature gate for AI features
    if (option === "url" && !canUseRemixAI) {
      handleFeatureGate(event, "website URL");
      return;
    }

    if (option === "image" && !canUseRemixAI) {
      handleFeatureGate(event, "image upload");
      return;
    }

    if (option === "chat" && !canUseRemixFromChat) {
      const requiredPlan = getRequiredPlanForRemixFromChat();
      subscriptions.upgrade_modal(
        event,
        false,
        `use Remix from Chat to generate a template`,
        requiredPlan,
        `Triggered feature CTA – Remix from Chat`
      );
      return;
    }

    // Don't allow selecting "scratch" if uploadUrl is false (section context)
    if (option === "scratch" && !uploadUrl) {
      return;
    }

    setSelectedOption(option);
    setDropdownOpen(false);

    if (option === "image") {
      uploads.select("callback", {
        environment: environment,
        path: `/websites/${websiteId}/`,
        website_id: websiteId,
        library: false,
        onUploadStarted: function (file) {
          setRemixLoading(true);
        },
        callback: function (file_url) {
          if (mountedRef.current) {
            imageInput.current.value = file_url;
          }

          setRemixLoading(false);
          remixSubmit();
        },
      });
    } else if (option === "scratch") {
      // Handle "From Scratch" option - could trigger a different flow
      if (typeof window.userEvent === "function") {
        window.userEvent("Remix - From Scratch Selected");
      }
    }
  };

  const triggerFilePicker = () => {
    uploads.select("callback", {
      environment: environment,
      path: `/websites/${websiteId}/`,
      website_id: websiteId,
      library: false,
      callback: function (file_url) {
        if (mountedRef.current) {
          imageInput.current.value = file_url;
          // After file is selected, automatically trigger remix
          remixSubmit();
        }
      },
    });
  };

  const getOptionIcon = (option) => {
    switch (option) {
      case "url":
        return (
          <svg
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.03376 8.14282L4.35867 9.81791C3.73307 10.4435 3.37331 11.2947 3.37988 12.1893C3.38646 13.084 3.73835 13.9404 4.39363 14.5754C5.02864 15.2307 5.88524 15.5826 6.77971 15.5892C7.69457 15.5959 8.52565 15.2564 9.15129 14.6308L10.8264 12.9557M12.966 10.857L14.6411 9.18191C15.2667 8.55631 15.6264 7.70509 15.6199 6.81048C15.6133 5.91587 15.2614 5.05946 14.6061 4.42441C13.9713 3.78954 13.1148 3.43764 12.2202 3.43106C11.3256 3.42449 10.4742 3.76383 9.84861 4.38944L8.17352 6.06453M6.95971 11.9953L11.985 6.97001"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "image":
        return (
          <svg
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M4.10078 16.7H14.9008C15.8949 16.7 16.7008 15.8517 16.7008 14.8053V4.19479C16.7008 3.14835 15.8949 2.30005 14.9008 2.30005H4.10078C3.10667 2.30005 2.30078 3.14835 2.30078 4.19478V14.8053C2.30078 15.8517 3.10667 16.7 4.10078 16.7Z"
              stroke="#A1A8C1"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5.00078 12.2H14.0008L11.00078 6.94996L8.75078 10.325L7.25078 8.82496L5.00078 12.2Z"
              stroke="#A1A8C1"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "scratch":
        return (
          <svg
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.5 2.30005V16.7M2.30078 9.5H16.7008"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "chat":
        return (
          <svg
            width="19"
            height="19"
            viewBox="0 0 19 19"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.5 9.5C2.5 5.63401 5.63401 2.5 9.5 2.5C13.366 2.5 16.5 5.63401 16.5 9.5C16.5 13.366 13.366 16.5 9.5 16.5H2.5L2.5 9.5Z"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const remixSubmit = async () => {
    // Check if chat option is selected - open playbook modal
    if (selectedOption === "chat") {
      if (typeof window.userEvent === "function") {
        window.userEvent("Remix - From Chat Selected");
      }
      document.dispatchEvent(new CustomEvent("playbook-chat:open"));
      onRemixFromChat?.();
      return;
    }

    // Check if scratch option is selected - skip to layout screen
    if (selectedOption === "scratch") {
      if (typeof window.userEvent === "function") {
        window.userEvent("Remix - From Scratch Started");
      }
      // Skip directly to layout screen (step 2) by calling onUploadSuccess with a special flag
      onUploadSuccess?.("scratch");
      return;
    }

    // Check if image option is selected and no URL is provided
    if (
      selectedOption === "image" &&
      (!imageInput.current?.value || imageInput.current.value.trim() === "")
    ) {
      triggerFilePicker();
      return;
    }

    const onStart = onUploadStart?.(imageInput.current?.value);
    if (onStart === false) {
      return;
    }

    setRemixLoading(true);
    remixSubmitBtn.current.disabled = true;
    inputError.current.style.display = "none";

    const { valid, imagePath } = await validateImageURL(
      imageInput.current?.value
    );

    if (!valid) {
      inputError.current.style.display = "block";
      remixSubmitBtn.current.disabled = false;
      setRemixLoading(false);
      return;
    }

    let processedImagePath = imagePath;

    try {
      if (!mountedRef.current) return;
      subtitleRef.current.textContent = "Processing image...";

      if (typeof window.userEvent === "function") {
        window.userEvent("Remix - Campaign Started");
      }

      if (onUploadProcessing) {
        try {
          const uploadProcessing = await onUploadProcessing(
            imagePath,
            subtitleRef
          );
          if (uploadProcessing) {
            processedImagePath = uploadProcessing;
          }
        } catch (error) {
          console.error("Error in upload processing:", error);
          throw error;
        }
      }

      onUploadSuccess?.(processedImagePath);
      subtitleRef.current.textContent = defaultSubtitle;
    } catch (error) {
      if (!mountedRef.current) return;

      inputError.current.style.display = "block";
      inputError.current.textContent = "Something went wrong";
    } finally {
      if (!mountedRef.current) return;

      setRemixLoading(false);
      remixSubmitBtn.current.disabled = false;
      subtitleRef.current.textContent = defaultSubtitle;
    }
  };

  const validateImageURL = (url) => {
    return new Promise((resolve) => {
      const img = new Image();

      img.src = url;
      img.onload = () => resolve({ valid: true, imagePath: url });
      img.onerror = () => {
        try {
          new URL(url);
          const parsedUrl = `https://cdn.filestackcontent.com/${filestackApiKey}/urlscreenshot=agent:desktop,delay:5000/${encodeURIComponent(
            url
          )}`;
          resolve({ valid: true, imagePath: parsedUrl });
        } catch (e) {
          resolve({ valid: false });
        }
      };
    });
  };

  return (
    <React.Fragment>
      <div
        className={`remix-uploader ${props.className ? props.className : ""}`}
      >
        <div className="input-box">
          <div className="headline">Generate a template. Remix with AI.</div>
          <p ref={subtitleRef} className="subtitle">
            {defaultSubtitle}
          </p>

          <div className="inline-form">
            <div className="input-selectors" ref={dropdownRef}>
              <div
                className="selector-btn active dropdown-trigger"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {getOptionIcon(selectedOption)}
                <div className="label">
                  {selectedOption === "image"
                    ? "Image"
                    : selectedOption === "scratch"
                    ? "Scratch"
                    : selectedOption === "chat"
                    ? "Chat"
                    : "Website"}
                </div>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={`transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {dropdownOpen && (
                <div className="dropdown-menu" style={{ display: "block" }}>
                  {uploadUrl && (
                    <div
                      className={`dropdown-item ${
                        selectedOption === "scratch" ? "active" : ""
                      }`}
                      onClick={(e) => handleOptionSelect("scratch", e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M15.3754 11.9995H12.0004M12.0004 11.9995H8.62537M12.0004 11.9995V15.3744M12.0004 11.9995L12.0004 8.62447M21 6.37498L21 17.625C21 19.489 19.489 21 17.625 21H6.375C4.51104 21 3 19.489 3 17.625V6.37498C3 4.51103 4.51104 3 6.375 3H17.625C19.489 3 21 4.51103 21 6.37498Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="label">From Scratch</div>
                    </div>
                  )}

                  {uploadUrl && showRemixFromChat && (
                    <div
                      className={`dropdown-item ${
                        selectedOption === "chat" ? "active" : ""
                      }`}
                      onClick={(e) => handleOptionSelect("chat", e)}
                    >
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.5 9.5C2.5 5.63401 5.63401 2.5 9.5 2.5C13.366 2.5 16.5 5.63401 16.5 9.5C16.5 13.366 13.366 16.5 9.5 16.5H2.5L2.5 9.5Z"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="label">
                        Remix from Chat
                        <span className="relative ml-2 top-[-3px] bg-blue-600 py-1 px-2.5 rounded text-sm text-white font-medium">
                          AI BETA
                        </span>
                      </div>
                    </div>
                  )}

                  {uploadUrl && (
                    <div
                      className={`dropdown-item ${
                        selectedOption === "url" ? "active" : ""
                      }`}
                      onClick={(e) => handleOptionSelect("url", e)}
                    >
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6.03376 8.14282L4.35867 9.81791C3.73307 10.4435 3.37331 11.2947 3.37988 12.1893C3.38646 13.084 3.73835 13.9404 4.39363 14.5754C5.02864 15.2307 5.88524 15.5826 6.77971 15.5892C7.69457 15.5959 8.52565 15.2564 9.15129 14.6308L10.8264 12.9557M12.966 10.857L14.6411 9.18191C15.2667 8.55631 15.6264 7.70509 15.6199 6.81048C15.6133 5.91587 15.2614 5.05946 14.6061 4.42441C13.9713 3.78954 13.1148 3.43764 12.2202 3.43106C11.3256 3.42449 10.4742 3.76383 9.84861 4.38944L8.17352 6.06453M6.95971 11.9953L11.985 6.97001"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="label">
                        Website URL
                        <span className="relative ml-2 top-[-3px] bg-blue-600 py-1 px-2.5 rounded text-sm text-white font-medium">
                          AI BETA
                        </span>
                      </div>
                    </div>
                  )}

                  {uploadImage && (
                    <div
                      className={`dropdown-item ${
                        selectedOption === "image" ? "active" : ""
                      }`}
                      onClick={(e) => handleOptionSelect("image", e)}
                    >
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.10078 16.7H14.9008C15.8949 16.7 16.7008 15.8517 16.7008 14.8053V4.19479C16.7008 3.14835 15.8949 2.30005 14.9008 2.30005H4.10078C3.10667 2.30005 2.30078 3.14835 2.30078 4.19478V14.8053C2.30078 15.8517 3.10667 16.7 4.10078 16.7Z"
                          stroke="#A1A8C1"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.00078 12.2H14.0008L11.00078 6.94996L8.75078 10.325L7.25078 8.82496L5.00078 12.2Z"
                          stroke="#A1A8C1"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="label">
                        Image upload
                        <span className="relative ml-2 top-[-3px] bg-blue-600 py-1 px-2.5 rounded text-sm text-white font-medium">
                          AI BETA
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedOption === "scratch" || selectedOption === "chat" ? (
              <button
                ref={remixSubmitBtn}
                onClick={remixSubmit}
                className="remix-btn"
                style={{ width: "100%", justifyContent: "center" }}
              >
                {remixLoading ? (
                  <span className="cf-loading-spinner" />
                ) : (
                  <React.Fragment>
                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.6184 2.30005L12.9911 6.00971L16.7008 7.3824L12.9911 8.7551L11.6184 12.4648L10.2457 8.7551L6.53608 7.3824L10.2457 6.00971L11.6184 2.30005Z"
                        stroke="white"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M5.26549 10.7706L6.46461 12.5362L8.23019 13.7353L6.46461 14.9345L5.26549 16.7L4.06637 14.9345L2.30078 13.7353L4.06637 12.5362L5.26549 10.7706Z"
                        stroke="white"
                        strokeWidth="1.3"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Remix
                    {selectedOption === "scratch"
                      ? " From Scratch →"
                      : selectedOption === "chat"
                      ? " From Chat →"
                      : ""}
                  </React.Fragment>
                )}
              </button>
            ) : (
              <React.Fragment>
                <div className="input-url">
                  <small
                    ref={inputError}
                    className="error-text"
                    style={{
                      position: "absolute",
                      fontSize: "10px",
                      top: "-17px",
                      color: "orangered",
                      display: "none",
                    }}
                  >
                    {selectedOption === "url" ? "Please enter a valid URL" : ""}
                  </small>
                  <input
                    ref={imageInput}
                    type="text"
                    placeholder={
                      selectedOption === "url"
                        ? "Paste website URL"
                        : "Image URL"
                    }
                    className="input"
                  />
                </div>

                <button
                  ref={remixSubmitBtn}
                  onClick={remixSubmit}
                  className="remix-btn"
                >
                  {remixLoading ? (
                    <span className="cf-loading-spinner" />
                  ) : (
                    <React.Fragment>
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.6184 2.30005L12.9911 6.00971L16.7008 7.3824L12.9911 8.7551L11.6184 12.4648L10.2457 8.7551L6.53608 7.3824L10.2457 6.00971L11.6184 2.30005Z"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.26549 10.7706L6.46461 12.5362L8.23019 13.7353L6.46461 14.9345L5.26549 16.7L4.06637 14.9345L2.30078 13.7353L4.06637 12.5362L5.26549 10.7706Z"
                          stroke="white"
                          strokeWidth="1.3"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Remix
                    </React.Fragment>
                  )}
                </button>
              </React.Fragment>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default RemixUploader;
